import React, { useState } from 'react';
import { format } from 'date-fns';
import { HiOutlineTrash } from 'react-icons/hi';
import useAdmin from '../../../../hooks/useAdmin';
import './ReportDetailView.scss';

const ReportDetailView = ({ report, onClose, onStatusUpdate, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { isAdmin } = useAdmin();
  
  if (!report) return null;
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let date;
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        // Firebase Timestamp object - convert seconds to milliseconds
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'number') {
        // Handle both seconds and milliseconds
        date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
      } else {
        date = new Date(timestamp);
      }
      
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      console.error('Error formatting date:', error, timestamp);
      return 'Invalid Date';
    }
  };
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_testing: { color: 'warning', text: 'Pending Testing' },
      test_report_available: { color: 'info', text: 'Test Report Available' },
      assigned_to_developer: { color: 'primary', text: 'Assigned to Developer' },
      tested_and_resolved: { color: 'success', text: 'Tested and Resolved' }
    };

    const config = statusConfig[status] || { color: 'secondary', text: status };
    return <span className={`status-badge ${config.color}`}>{config.text}</span>;
  };

  const handleDeleteConfirm = async () => {
    if (!onDelete) return;
    
    setIsDeleting(true);
    try {
      await onDelete(report.id);
      onClose(); // Close modal after successful deletion
    } catch (error) {
      console.error('Error deleting report:', error);
      // Keep modal open on error so user can try again
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };
  
  return (
    <div className="modal-overlay">
      <div className="modal-content report-detail-modal">
        <div className="modal-header">
          <h3>Testing Report Details - ID: {report.id}</h3>
          <div className="header-actions">
            {isAdmin && (
              <button 
                className="delete-button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isDeleting}
                title="Delete Report"
              >
                <HiOutlineTrash />
                Delete
              </button>
            )}
            <button 
              className="close-button"
              onClick={onClose}
            >
              &times;
            </button>
          </div>
        </div>
        <div className="modal-body">
          <div className="report-detail-header">
            <div className="assignee-info">
              <h4>Assignee</h4>
              <p className="assignee-name">{report.assignedTo?.name || 'N/A'}</p>
              <p className="assignee-email">{report.assignedTo?.email || 'N/A'}</p>
            </div>
            
            <div className="status-info">
              <h4>Status</h4>
              <div className="status-badge-container">
                {getStatusBadge(report.status)}
                <button 
                  className="update-status-button"
                  onClick={onStatusUpdate}
                >
                  Update Status
                </button>
              </div>
            </div>
            
            <div className="dates-info">
              <h4>Dates</h4>
              <p><strong>Created:</strong> {formatDate(report.createdAt)}</p>
              <p><strong>Last Updated:</strong> {formatDate(report.lastUpdated)}</p>
            </div>
            
            <div className="week-info">
              <h4>Week Range</h4>
              <p>{report.weekRange || 'N/A'}</p>
            </div>
          </div>
          
          <div className="report-detail-content">
            <div className="left-column">
              <div className="games-section">
                <h4>Games Tested</h4>
                {report.selectedGames?.length > 0 ? (
                  <ul className="games-list">
                    {report.selectedGames.map((game, index) => (
                      <li key={index}>{game}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-content">No games selected</p>
                )}
              </div>
            </div>
            
            <div className="right-column">
              <div className="feedback-section">
              <h4>Testing Feedback</h4>
              
              {report.tutorialFeedback ? (
                <div className="feedback-item">
                  <h5>Tutorial Feedback</h5>
                  <p className="feedback-content">{report.tutorialFeedback}</p>
                </div>
              ) : null}
              
              {report.levelsFeedback ? (
                <div className="feedback-item">
                  <h5>Levels Feedback</h5>
                  <p className="feedback-content">{report.levelsFeedback}</p>
                </div>
              ) : null}
              
              {report.positiveAspects ? (
                <div className="feedback-item">
                  <h5>Positive Aspects</h5>
                  <p className="feedback-content">{report.positiveAspects}</p>
                </div>
              ) : null}
              
              {report.improvementSuggestions ? (
                <div className="feedback-item">
                  <h5>Improvement Suggestions</h5>
                  <p className="feedback-content">{report.improvementSuggestions}</p>
                </div>
              ) : null}
              
              {report.overallExperience ? (
                <div className="feedback-item">
                  <h5>Overall Experience</h5>
                  <p className="feedback-content">{report.overallExperience}</p>
                </div>
              ) : null}
              
              {!report.tutorialFeedback && 
               !report.levelsFeedback && 
               !report.positiveAspects && 
               !report.improvementSuggestions && 
               !report.overallExperience && (
                <p className="no-content">No feedback provided yet</p>
              )}
            </div>
            
              <div className="bug-reports-section">
                <h4>Bug Reports {report.bugReports?.length > 0 ? `(${report.bugReports.length})` : ''}</h4>
                
                {report.bugReports?.length > 0 ? (
                  <div className="bug-reports-list">
                    {report.bugReports.map((bug, index) => (
                      <div key={bug.id || index} className="bug-report-item">
                        <h5>{bug.title || `Bug Report ${index + 1}`}</h5>
                        <p className="bug-description">{bug.description || 'No description provided'}</p>
                        
                        {bug.steps && (
                          <div className="bug-steps">
                            <h6>Steps to Reproduce</h6>
                            <p>{bug.steps}</p>
                          </div>
                        )}
                        
                        {bug.images?.length > 0 && (
                          <div className="bug-images">
                            <h6>Bug Images</h6>
                            <div className="images-grid">
                              {bug.images.map((image, imgIndex) => (
                                <a 
                                  key={imgIndex} 
                                  href={image.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="image-thumbnail"
                                >
                                  <img 
                                    src={image.url} 
                                    alt={`Bug screenshot ${imgIndex + 1}`}
                                    onError={(e) => {
                                      console.error('Bug image failed to load:', image.url);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-content">No bug reports submitted</p>
                )}
              </div>

              {/* Main Report Images Section */}
              {report.images && report.images.length > 0 && (
                <div className="main-images-section">
                  <h4>Report Screenshots ({report.images.length})</h4>
                  <div className="main-images-grid">
                    {report.images.map((image, index) => (
                      <div className="main-image-item" key={index}>
                        <a href={image.url} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={image.url} 
                            alt={image.name || `Report screenshot ${index + 1}`}
                            onError={(e) => {
                              console.error('Main report image failed to load:', image.url);
                              e.target.style.display = 'none';
                            }}
                          />
                        </a>
                        <div className="image-name">{image.name || `Screenshot ${index + 1}`}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button 
            className="close-button"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <div className="delete-confirm-header">
              <h3>Delete Report</h3>
            </div>
            <div className="delete-confirm-content">
              <p>Are you sure you want to delete this game testing report?</p>
              <p><strong>This action will permanently delete:</strong></p>
              <ul>
                <li>The testing report and all feedback</li>
                <li>All associated bug reports ({report.bugReports?.length || 0} reports)</li>
                <li>All uploaded images ({report.images?.length || 0} files)</li>
              </ul>
              <p className="warning">This action cannot be undone.</p>
            </div>
            <div className="delete-confirm-actions">
              <button 
                className="cancel-btn"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="delete-confirm-btn"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportDetailView; 